import { importFile } from "@/api";
import { AppStateAtom } from "@/store/AppStateAtom";
import { ClientStateAtom } from "@/store/ClientStateAtom";
import { SelectedFundAtom } from "@/store/FundStateAtom";
import { Fund } from "@/types/Fund";
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

interface UseImportFileController {
  selectedTab: string;
  setSelectedTab: (tab: string) => void;
  selectedTemplateId?: number;
  setSelectedTemplateId: (id?: number) => void;
  file?: File;
  setFile: (file?: File) => void;
  performImport: () => void;
  loading: boolean;
  context: string;
  selectedFund: Fund | undefined;
}

export const useImportFileController = (): UseImportFileController => {
  const [selectedTab, setSelectedTab] = useState("existing");
  const [file, setFile] = useState<File | undefined>(undefined);
  const [selectedTemplateId, setSelectedTemplateId] = useState<
    number | undefined
  >(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const selectedFund = useAtomValue(SelectedFundAtom);
  const clientData = useAtomValue(ClientStateAtom);

  const appState = useAtomValue(AppStateAtom);

  const [searchParams, _] = useSearchParams();

  const context = searchParams.get("context") ?? "client";
  const templateId = searchParams.get("templateId");

  const navigate = useNavigate();

  const performImport = useCallback(async () => {
    if (!file || !selectedTemplateId) {
      console.error("File or template ID is not set");
      return;
    }

    if (context !== "client" && selectedFund === undefined) {
      console.error("Fund is not set");
      return;
    }

    setLoading(true);
    console.log("Importing file", file);
    await importFile(
      file,
      selectedTemplateId,
      selectedFund?.fundId,
      undefined,
      appState.email
    );
    setLoading(false);

    // go back to the imports page with appropriate type filter
    const typeParam = context === "client" ? "" : `&type=${context}`;
    navigate(`/app/imports?context=${context}${typeParam}`);
  }, [file, selectedTemplateId, context]);

  useEffect(() => {
    if (templateId) {
      setSelectedTemplateId((prevState) => {
        if (prevState !== undefined) {
          return prevState;
        }
        const id = parseInt(templateId, 10);
        if (isNaN(id)) {
          return undefined;
        }
        return id;
      });
    }
  }, [templateId]);

  return {
    selectedTab,
    setSelectedTab,
    selectedTemplateId,
    setSelectedTemplateId,
    performImport,
    file,
    setFile,
    loading,
    selectedFund,
    context,
  };
};
